<template>
  <div
    v-if="draft && ($login.can('newsletter-drafts.update') || $login.can('newsletter-drafts.update.mine'))"
    class="newsletter-draft"
  >
    <page-header header="Create new newsletter" />

    <stepper
      :steps="steps"
      :active-step-index="activeStepIndex"
      @change-step="navigateToStep"
    />

    <keep-alive
      v-if="draft.steps"
      max="5"
    >
      <component
        ref="component"
        :key="`${newsletterType ? newsletterType.slug : ''}-${steps[activeStepIndex].name}`"
        :is="steps[activeStepIndex].component"
        :draft="draft"
        :newsletter-type="newsletterType"
        :newsletter-types="activeStepIndex === 0 ? newsletterTypes : undefined"
        :errors="errorData"
        :created-at="createdAt"
        :highlight-fields="highlightFields"
        :email-html="emailHtml"
        @clearedDraft="onClearedDraft"
        @update="onUpdate"
        @update2="updateEverything"
        @getPreview="() => getPreview(false)"
        @force-next="forceNext"
      />
    </keep-alive>

    <div class="d-flex justify-content-between mb-4">
      <div>
        <button
          class="btn btn-outline-danger"
          @click="$modal.show('confirm-delete')"
        >
          Delete
        </button>
      </div>

      <div class="d-flex justify-content-between mb-4">
        <button
          v-if="activeStepIndex === steps.length - 1"
          class="btn btn-outline-primary ml-3"
          @click="sendTest"
        >
          Send Test
        </button>

        <button
          class="btn btn-outline-primary ml-3"
          @click="saveDraft(true)"
        >
          Save
        </button>

        <button
          v-if="activeStepIndex !== 0"
          class="btn btn-outline-primary ml-3"
          @click="goBack"
        >
          Back
        </button>
        <button
          v-if="activeStepIndex !== steps.length - 1"
          class="btn btn-primary ml-3"
          @click="goNext"
        >
          Next
        </button>
        <button
          v-if="activeStepIndex === steps.length - 1"
          class="btn btn-outline-primary ml-3"
          @click="publishAndSendDraft"
        >
          {{isUpdating ? 'Update' : 'Publish'}} &amp; Send Now
        </button>
        <button
          v-if="activeStepIndex === steps.length - 1"
          class="btn btn-primary ml-3"
          :class="{disabled: publishing}"
          @click="publishDraft"
        >
          {{isUpdating ? 'Update' : 'Publish'}}
        </button>
      </div>
    </div>

    <confirm
      v-if="draft"
      :name="draft.subject || 'Untitled'"
      @confirmed="deleteDraft"
    />
  </div>
</template>

<script>
import gql from 'graphql-tag';
import VueScrollTo from 'vue-scrollto'
import merge from 'lodash.merge'
import {mergeDeepRight} from 'ramda'
import get from 'lodash.get'
import moment from 'moment'
import striptags from 'striptags'
import gateway from '@/gateway'
import Stepper from '@/components/stepper/Stepper'
import Search from '@/components/Search'
import Confirm from '@/components/Confirm'
import {successNotification, errorNotification} from '@/notifications'
import {debounce} from '@/helpers'
import Steps from './steps'
import sanitizeHtml from 'sanitize-html'

export default {
  components: {
    Stepper,
    Search,
    Confirm,
  },

  props: {
    isUpdating: {
      type: Boolean,
      required: false,
    },
    data: {
      type: Object,
      required: true,
    },
    newsletterTypes: {
      type: Array,
      required: true,
    },
    createdAt: {
      required: true,
      type: String,
    },
  },

  metaInfo () {
    return {
      title: this.draft && this.draft.subject ? this.draft.subject : 'Create Newsletter',
    }
  },

  data () {
    return {
      draft: {...this.data},
      activeStepIndex: 0,
      picture: [],
      error: null,
      errorData: null,
      url: process.env.VUE_APP_API_ENDPOINT,
      highlightFields: false,
      hasPopulated: false,
      emailHtml: '',
      publishing: false,
    }
  },

  created () {
    this.populate()
  },

  computed: {
    newsletterType () {
      return this.newsletterTypes.find((t) => t.id === this.draft.newsletterTypeId)
    },

    steps () {
      const steps = this.newsletterType ? Steps[this.newsletterType.slug] : Steps.default
      // TODO: race condition -> fix the whole this.populate & this.steps process
      if (this.hasPopulated) {
        return steps.map((step, index) => ({...step, isValid: this.isValid(index)}))
      }
      return steps
    },

    headers () {
      return {
        Authorization: 'Bearer ' + this.$login.getToken(),
      }
    },
  },

  methods: {
    isValid (step) {
      if (step === -1) {
        return true
      }
      switch (get(this.newsletterType, 'slug')) {
        case 'default': {
          return false
        }

        case 'blank-template': {
          if (step > 0) {
            return !this.draft.steps.body.html || this.draft.steps.body.html.length > 0
          }
          return true
        }

        case 'end-of-day-wrap': {
          if (step > 0) {
            return !this.draft.steps.body.html || this.draft.steps.body.html.length > 0
          }
          return true
        }

        case 'friday-five': {
          if (step === 0) {
            const {title = null, article = null, synopsis = null} = this.draft.steps.overview.firstArticle || {}
            return title && synopsis && (article ? article.id : false)
          } else if (step === 1) {
            const {article2, article3, article4, article5} = this.draft.steps.secondAndFifthArticles
            return article2 && article2.article && article3.article && article4.article && article5.article
          }

          return false
        }

        case 'weekly-round-up': {
          /* eslint-disable */
          if (step === 0) {
            const {analyst_article = null, analyst_title = null, analyst_synopsis = null} = this.draft.steps.overview
            return analyst_title && analyst_synopsis && analyst_article && (analyst_article ? analyst_article.id : false)
          } else if (step === 1) {
            const [highlights1, highlights2, highlights3] = Object.values(this.draft.steps.highlights)
            return highlights1 && highlights1.article && highlights2.article && highlights3.article
          } else if (step === 2) {
            const {most_read1, most_read2, most_read3, chart_image, chart_source, chart_article} = this.draft.steps.ephemera
            return most_read1 && most_read1.article && most_read2.article && most_read3.article
              && most_read1.synopsis && most_read2.synopsis && most_read3.synopsis
              && chart_image && chart_source && chart_article && chart_article.id
          }

          return false
        }

        case 'tellimer-today': {
          if (step === 0) {
            return true
          } else if (step === 1) {
            let isValid = true

            if (!this.draft.steps.articles || !this.draft.steps.articles.articles) {
              return false
            }

            this.draft.steps.articles.articles.forEach(article => {
              if(!article){ return }
              isValid = article.synopsis != '' ? !!article.synopsis : true
            })

            return isValid
          } else if (step === 2) {
            return true
          }
        }

        case 'tellimer-today-weekend-edition': {
         if (step === 0) {
            return true
          } else if (step === 1) {
            let isValid = true

            Object.keys(this.draft.steps.articles).forEach(key => {
              const article = this.draft.steps.articles[key] || {}
              const {marketingBullet1, marketingBullet2, marketingBullet3, title, synopsis}=article
              isValid = (marketingBullet1 && marketingBullet2 && marketingBullet3 && title && synopsis) && isValid
            })

            return isValid
          } else if (step === 2) {
            return true
          }
          return true
        }

        case 'tellimer-markets-briefing': {
          if (step === 1) {
            for (const article of Object.values(this.draft.steps.summaries)) {
              const syn = striptags(article?.synopsis)
              if (article?.title && (syn.length === 0 || syn.length > 700)) {
                return false
              }
            }
          }
          return true
        }

        default: {
          return false
        }
      }
    },

    delayedSave: debounce(function () {
      this.saveDraft()
    }, 500),

    populate () {
      this.draft.steps = this.draft.steps || {}
      if (this.draft.newsletterTypeId) {
        const sections = this.draft.sections.sort((a, b) => a.order > b.order)
        const stepsKeys = this.steps.map((s) => s.name).filter((k) => k !== 'preview')

        // populate data from each section
        stepsKeys.forEach((step) => {
          const section = sections.find((s) => s.title === step)
          this.draft.steps[step] = {}

          if (section) {
            this.draft.steps[step] = section.body || {}
          }
        })
      }
      this.hasPopulated = true
    },

    async deleteDraft () {
      try {
        // eslint-disable-next-line camelcase
        const {newsletterDraftId, subject} = this.draft

        // eslint-disable-next-line camelcase
        await gateway.delete('/newsletters/drafts/' + newsletterDraftId)

        this.$notify(successNotification(`${subject || 'The Newsletter Draft'} has been deleted.`))
        this.$router.push({name: 'newsletters'})
      } catch (error) {
        // TODO: handle this?
        console.error(error)
      }
    },

    getDefaultFromName () {
      if (this.newsletterType && this.newsletterType.slug === 'blank-template') {
        return this.$login.user.name
      }

      return ''
    },

    getDefaultFromEmail () {
      if (this.newsletterType && this.newsletterType.slug === 'blank-template') {
        return this.$login.user.email
      }

      return ''
    },

    async getPreview (save = true) {
      if (save) {
        await this.saveDraft()
      }
      const response = await gateway.get(`/newsletters/drafts/${this.draft.newsletterDraftId}/preview`)
      this.emailHtml = response.data.data.html
    },

    async saveDraft (notify = false) {
      try {
        if (this.newsletterType && !this.draft.newsletterDraftId) {
          this.draft.alertLists = this.newsletterType.alertLists.map((l) => l.id)
          this.draft.fromName = this.newsletterType.alertFromName || this.getDefaultFromName()
          this.draft.fromEmail = this.newsletterType.alertFromEmail || this.getDefaultFromEmail()
          this.draft.sendAt = moment().add(30, 'minutes').toISOString()
        }

        // store briefings
        const tellimerToday = this.newsletterTypes.find(t => t.slug === 'tellimer-today')

        if (!this.draft.newsletterDraftId && this.draft.newsletterTypeId === tellimerToday.id) {
          await this.storeBriefiengs()
        }

		// organize data
        const {steps, sections, ...data} = this.draft
        console.log(sections)
        const updatedSections = []
        const stepsOder = this.steps.reduce((a, b, c) => {
          a[b.name] = c
          return a
        }, {})

        // store information of each section from each step
        Object.keys(steps).forEach((step) => {
          const data = steps[step]
          console.log(step, typeof data, data)
          let section = sections.find((s) => s.title === step)

          if (section) {
            section.body = data
          } else {
            section = {
              title: step,
              body: data,
            }
          }

          // force the order of sections based on the steps definition
          section.order = stepsOder[step]

          updatedSections.push(section)
        })

        data.sections = updatedSections
        this.draft.sections = data.sections

        const requestData = {
          subject: data.subject,
          newsletterTypeId: data.newsletterTypeId,
          sections: data.sections || [],
          sendAt: data.sendAt,
          alertLists: data.alertLists,
          fromName: data.fromName,
          fromEmail: data.fromEmail,
          preheaderText: data.preheaderText,
        }

        let url = '/newsletters/drafts'
        if (this.draft.newsletterDraftId) {
          url += `/${data.newsletterDraftId}/revisions`
        }
        const response = await gateway.post(url, requestData)

        if (!this.draft.newsletterDraftId) {
          this.draft.newsletterDraftId = response.data.data._id
          history.replaceState({}, null, this.$router.resolve({name: 'newsletters.draft', params: {draftId: this.draft.newsletterDraftId}}).href)
        }

        if (notify) {
          this.$notify(successNotification('Successfully saved.'))
        }

        if (typeof this.$refs.component.onUpdated === 'function') {
          this.$refs.component.onUpdated()
        }
      } catch (error) {
        console.error(error)
        // TODO: display error message?
      }
    },

    async publishAndSendDraft () {
      this.draft.sendAt = moment().toISOString()
      await this.publishDraft()
    },

    async publishDraft () {
      if (!this.publishing) {
        this.publishing = true
        try {
          console.log(this.draft.newsletterDraftId)
          await this.saveDraft()
          await gateway.patch(`/newsletters/drafts/${this.draft.newsletterDraftId}/publish`, {
            sendAt: this.draft.sendAt,
            alertLists: this.draft.alertLists,
          })

          this.$notify(successNotification(`${this.draft.subject || 'The Newsletter Draft'} has been published.`))
          this.$router.push({name: 'newsletters'})
        } catch (error) {
          console.error(error)

          if (error.response && error.response.data) {
            const responseErrors = error.response.data.reduce((a, b, c) => {
              a[b.path[0]] = [b.message]; return a
            }, {})
            this.errorData = this.errorData || {}
            this.errorData.errors = {...this.errorData.errors, ...responseErrors}
          }

          this.scrollIntoView()
          this.$notify(errorNotification(error))
        }
        this.publishing = false
      }
    },

    async sendTest () {
      console.log(this.draft)
      await this.saveDraft()
      await gateway.post(`/newsletters/drafts/${this.draft.newsletterDraftId}/test`, {})
      this.$notify(successNotification(`Sent a test to ${this.$login.getUser().email}`))
    },

    async goBack () {
      await this.navigateToStep(Math.max(0, this.activeStepIndex - 1))
    },

    async goNext () {
      await this.navigateToStep(Math.min(this.steps.length - 1, this.activeStepIndex + 1))
    },

    async forceNext() {
      await this.goNext()
    },

    async storeBriefiengs() {
      const result = await this.$apollo.query({
        fetchPolicy: 'network-only',
        query: gql`query {
                homepage {
                  value {
                    briefings {
                      thumbnail
                      content
                      link
                    }
                  }
                }
              }`
      })

      if (result && result.data) {
        // const briefings = result.data.homepage.value.briefings
        const articles = result.data.homepage.value.briefings
        this.draft.steps.articles = {articles: []}

        for (let i = 0; i < articles.length; i++) {
          const article = articles[i];

          this.draft.steps.articles.articles.push({
            title: article.content?.substring(0, 50) || '',
            synopsis: sanitizeHtml(article.content, {
              allowedTags: [ 'h3', 'h4', 'h5', 'h6', 'blockquote', 'p', 'a', 'ul', 'ol',
                'nl', 'li', 'abbr', 'hr', 'br', 'div',
                'table', 'thead', 'caption', 'tbody', 'tr', 'th', 'td', 'img', 'picture', 'source', 'iframe'
              ],
              allowedAttributes: {
                a: [ 'href', 'name', 'target' ],
                img: [ 'src', 'alt', 'loading' ],
                picture: ['class'],
                source: ['srcset', 'media'],
                iframe: ['src', 'frameborder', 'width', 'style', 'loading'],
              },
              selfClosing: [ 'img', 'br', 'hr', 'source' ],
            }),
            link: article.link
          })
        }
      }
    },

    async navigateToStep (index) {
      await this.saveDraft()

      if (!this.isValid(index-1)) {
        return this.highlightRequired()
      } else {
        this.activeStepIndex = index
        this.scrollIntoView()
      }
    },

    highlightRequired () {
      this.highlightFields = true
      this.$notify(errorNotification('Please fill out all fields before continuing.'))
      this.$nextTick(() => {
        const target = document.querySelector('.is-invalid').parentElement
        this.$scrollTo(target)
      })
      setTimeout(() => this.highlightFields = false, 7000)
    },

    onUpdate (draftSlice = {}) {
      this.draft = merge(
        this.draft,
        draftSlice,
      )
      this.delayedSave()
    },

    updateEverything (draft) {
      this.draft = draft
      this.delayedSave()
    },

    scrollIntoView () {
      const target = document.querySelector('.newsletter-draft')
      VueScrollTo.scrollTo(target, 500)
    },

    onClearedDraft (newDraft) {
      this.draft = newDraft
    },
  },
}
</script>

<style lang="scss">
.draft-textarea {
  &--invalid {
    border: 1px solid $red;
  }
}
</style>
